import { useEffect, useRef, type DependencyList } from 'react';
import type { Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { Websocket } from '../services/websocket';
import Authentication from '../services/authentication';
import { ensureSessionValidOrRefresh } from '../services/ensure-session';
import { authSessionLog } from '../services/auth-session-logger';
import store, { AppState } from '../stores';
import { useHistory } from 'react-router';

export type SocketEventAuthMode = 'before' | 'after' | 'none';

const authentication = new Authentication();

/**
 * WebSocket イベントの購読と認証ガードをまとめる。
 * handler は ref 経由で常に最新版が呼ばれる（deps に handler を含めなくてよい）。
 * force reconnect で socket が差し替わったときも onSocketReplaced で再登録する。
 */
export function useSocketEvent<T = unknown>(
  event: string,
  handler: (payload: T) => void,
  deps: DependencyList,
  authMode: SocketEventAuthMode = 'before',
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const history = useHistory();
  const accessTokenExpiresAt = useSelector(
    (state: AppState) => state.authenticated.accessTokenExpiresAt,
  );

  useEffect(() => {
    let attachedSocket: Socket | undefined;

    const wrappedHandler = (payload: T) => {
      void (async () => {
        if (authMode === 'before') {
          const sessionResult = await ensureSessionValidOrRefresh();
          if (!sessionResult.ok) {
            if (sessionResult.reason === 'auth') {
              authSessionLog({
                level: 'warn',
                event: 'soft_logout',
                reason: 'auth',
                trigger: `useSocketEvent:${event}`,
                detail: 'socket event blocked due to session auth failure',
              });
              history.replace('/login');
            }
            return;
          }
          handlerRef.current(payload);
          return;
        }

        handlerRef.current(payload);

        if (authMode === 'after') {
          const accessTokenExpiresAt = store.getState().authenticated.accessTokenExpiresAt;
          if (authentication.isSessionValid(accessTokenExpiresAt)) {
            return;
          }
          const sessionResult = await ensureSessionValidOrRefresh();
          if (!sessionResult.ok && sessionResult.reason === 'auth') {
            authSessionLog({
              level: 'warn',
              event: 'soft_logout',
              reason: 'auth',
              trigger: `useSocketEvent:${event}:after`,
              detail: 'socket event after-handler session auth failure',
            });
            history.replace('/login');
          }
        }
      })();
    };

    const attach = (socket: Socket | undefined): void => {
      if (attachedSocket) {
        attachedSocket.off(event, wrappedHandler);
      }
      attachedSocket = socket;
      if (socket) {
        socket.on(event, wrappedHandler);
      }
    };

    attach(Websocket.socket);
    const unsubscribe = Websocket.onSocketReplaced(attach);

    return () => {
      unsubscribe();
      if (attachedSocket) {
        attachedSocket.off(event, wrappedHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handler は ref で参照する
  }, [event, authMode, accessTokenExpiresAt, ...deps]);
}
